import { useState, useEffect, useMemo, useCallback } from 'react'
import { Clock, ChevronDown } from '@/ui/icons'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/components'
import { Button } from '@/ui/components'
import { Input } from '@/ui/components'
import { Label } from '@/ui/components'
import { formatTimeForDisplay } from '../../utils/timeCalculations'
import { cn } from '@/shared/utils'

interface WaypointTimeInputProps {
  value: string | null | undefined
  onChange: (value: string | null) => void
  placeholder?: string
  onBlur?: () => void
  onCancel?: () => void
  className?: string
}

/**
 * Parse time string to hours, minutes, and period (AM/PM)
 */
function parseTimeToComponents(timeString: string | null | undefined): {
  hours: number
  minutes: number
  period: 'AM' | 'PM'
} | null {
  if (!timeString) return null

  let hours: number
  let minutes: number

  // Handle HH:MM:SS format (database returns this)
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(timeString)) {
    const parts = timeString.split(':')
    hours = parseInt(parts[0], 10)
    minutes = parseInt(parts[1], 10)
  }
  // Handle HH:MM format
  else if (/^\d{1,2}:\d{2}$/.test(timeString)) {
    const parts = timeString.split(':')
    hours = parseInt(parts[0], 10)
    minutes = parseInt(parts[1], 10)
  }
  // Handle HHMM format (4 digits)
  else if (/^\d{4}$/.test(timeString)) {
    hours = parseInt(timeString.substring(0, 2), 10)
    minutes = parseInt(timeString.substring(2, 4), 10)
  } else {
    return null
  }

  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }

  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours

  return {
    hours: displayHours,
    minutes,
    period
  }
}

/**
 * Convert hours (1-12), minutes, and period to HH:MM format
 */
function componentsToTimeString(hours: number, minutes: number, period: 'AM' | 'PM'): string {
  let hour24 = hours
  if (period === 'AM' && hours === 12) {
    hour24 = 0
  } else if (period === 'PM' && hours !== 12) {
    hour24 = hours + 12
  }

  return `${String(hour24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * Modern time input component for waypoint times
 * Uses a popover with hour/minute selectors and AM/PM toggle
 * Stores time in HH:MM format (e.g., "10:30")
 */
/**
 * Create time slots for selection grid
 */
function createTimeSlots(startHour: number, endHour: number, intervalMinutes: number): string[] {
  const slots: string[] = []
  for (let hour = startHour; hour <= endHour; hour += 1) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`)
    }
  }
  return slots
}

/**
 * Parse human-readable time input to HH:MM format
 */
function parseHumanTimeInput(input: string): string | null {
  const trimmed = input.trim().toLowerCase()
  if (!trimmed) return null

  // First check if it's already in HH:MM format
  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    const [hours, minutes] = trimmed.split(':').map(Number)
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return trimmed
    }
    return null
  }

  // Try to parse various human-readable formats
  // Pattern: (1-2 digits)(optional :MM)(optional space)(optional am/pm)
  const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/)
  if (!match) return null

  let hour = Number.parseInt(match[1], 10)
  const minutes = Number.parseInt(match[2] ?? '0', 10)
  const suffix = match[3]

  if (Number.isNaN(hour) || Number.isNaN(minutes) || minutes > 59) {
    return null
  }

  // Handle 12-hour format with am/pm
  if (suffix) {
    if (hour === 12) {
      hour = suffix === 'am' ? 0 : 12
    } else if (suffix === 'pm') {
      hour += 12
    }
    // Validate hour is in valid range after conversion
    if (hour < 0 || hour > 23) {
      return null
    }
  } else {
    // 24-hour format - validate hour is in valid range
    if (hour > 23) {
      return null
    }
  }

  return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

export function WaypointTimeInput({
  value,
  onChange,
  placeholder = 'Pick an exact time',
  onBlur,
  onCancel,
  className = ''
}: WaypointTimeInputProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [manualTimeDisplay, setManualTimeDisplay] = useState('')
  const [manualTimeError, setManualTimeError] = useState<string | null>(null)

  const timeSlots = useMemo(() => {
    const slots = createTimeSlots(4, 22, 15)
    return slots
  }, [])

  // Initialize manual time display from value
  useEffect(() => {
    if (value) {
      const formatted = formatTimeForDisplay(value)
      setManualTimeDisplay(formatted ?? '')
      setManualTimeError(null)
    } else {
      setManualTimeDisplay('')
    }
  }, [value])

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      // Reset manual time display to current value when closing
      // Use a small delay to ensure any pending onChange has been processed
      setTimeout(() => {
        if (value) {
          const formatted = formatTimeForDisplay(value)
          setManualTimeDisplay(formatted ?? '')
        } else {
          setManualTimeDisplay('')
        }
        setManualTimeError(null)
        onBlur?.()
      }, 100)
    }
  }

  const handleCancel = () => {
    // Revert to original value
    if (value) {
      const formatted = formatTimeForDisplay(value)
      setManualTimeDisplay(formatted ?? '')
    } else {
      setManualTimeDisplay('')
    }
    setManualTimeError(null)
    setIsOpen(false)
    onCancel?.()
  }

  const handleClear = () => {
    onChange(null)
    setManualTimeDisplay('')
    setManualTimeError(null)
    setIsOpen(false)
    onBlur?.()
  }

  const handleTimeSelect = useCallback((timeValue: string) => {
    // IMPORTANT: Call onChange synchronously BEFORE closing the popover
    // This ensures the value is set before any state updates or closures happen
    onChange(timeValue)
    
    // Update manual time display to match the selected value
    const formatted = formatTimeForDisplay(timeValue)
    setManualTimeDisplay(formatted ?? '')
    setManualTimeError(null)
    
    // Close the popover after ensuring onChange has been called
    // Use requestAnimationFrame to ensure the onChange callback has been processed
    requestAnimationFrame(() => {
      setIsOpen(false)
    })
    
    // Don't call onBlur immediately - let the onChange handler manage the field state
    // The parent component will close the field after the update completes
  }, [onChange, value])

  const handleManualTimeCommit = () => {
    if (!manualTimeDisplay.trim()) {
      handleClear()
      return
    }

    const parsed = parseHumanTimeInput(manualTimeDisplay)
    if (!parsed) {
      setManualTimeError('Enter a valid time (e.g. 7:45 AM)')
      return
    }

    onChange(parsed)
    setManualTimeError(null)
    setIsOpen(false)
    onBlur?.()
  }

  const displayTime = value ? formatTimeForDisplay(value) : placeholder

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'flex w-full items-center justify-between rounded-xl border-earth-200 dark:border-earth-700 px-4 py-6 text-left text-base font-semibold text-earth-900 dark:text-foreground',
            !value && 'text-earth-500 dark:text-earth-400',
            className
          )}
          onClick={(e) => {
            e.stopPropagation()
          }}
          type="button"
        >
          <span>{displayTime}</span>
          <ChevronDown className="h-4 w-4 text-earth-500 dark:text-earth-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[22rem] p-0" 
        align="start"
        onClick={(e) => {
          e.stopPropagation()
        }}
        onPointerDown={(e) => {
          // Don't prevent pointer events - let clicks through
        }}
      >
        <div className="border-b border-earth-200 dark:border-earth-700 p-4">
          <Label className="text-xs font-semibold uppercase tracking-wide text-earth-500 dark:text-earth-400">
            Type a time
          </Label>
          <Input
            value={manualTimeDisplay}
            onChange={(event) => setManualTimeDisplay(event.target.value)}
            onBlur={handleManualTimeCommit}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleManualTimeCommit()
              }
            }}
            placeholder="e.g. 7:15 am"
            autoFocus
            className="mt-2"
          />
          {manualTimeError ? (
            <p className="mt-2 text-xs text-destructive">{manualTimeError}</p>
          ) : (
            <p className="mt-2 text-xs text-earth-500 dark:text-earth-400">
              Free-type anything — we'll format it for everyone else.
            </p>
          )}
        </div>
        <div className="max-h-64 overflow-y-auto p-3">
          <div className="grid grid-cols-3 gap-2">
            {timeSlots.map((slot, index) => {
              const displayTime = formatTimeForDisplay(slot)
              
              // Create handlers using IIFE to capture slot value in closure
              // This ensures each button gets the correct slot value, not the last one from the loop
              const handlers = (() => {
                const capturedSlot = slot // Capture in IIFE closure
                return {
                  handleMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault()
                    e.stopPropagation()
                    // Call handleTimeSelect with the captured slot value
                    handleTimeSelect(capturedSlot)
                  },
                  handleClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault()
                    e.stopPropagation()
                    // Also call handleTimeSelect on click as backup
                    // But check if it was already called in mouseDown to avoid double calls
                    handleTimeSelect(capturedSlot)
                  }
                }
              })()
              
              return (
                <button
                  key={slot}
                  type="button"
                  onClick={handlers.handleClick}
                  onMouseDown={handlers.handleMouseDown}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
                    'text-earth-700 dark:text-foreground border-earth-200 dark:border-earth-600',
                    'hover:border-adventure-500 hover:bg-adventure-50 dark:hover:border-adventure-500 dark:hover:bg-adventure-900/40',
                    value === slot && 'border-adventure-600 bg-adventure-50 text-adventure-900 dark:border-adventure-500 dark:bg-adventure-900/50 dark:text-adventure-200'
                  )}
                >
                  {displayTime}
                </button>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

